import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Smartphone, Globe, Check, ArrowRight } from "lucide-react";

export default function Comparison() {
	const { t } = useTranslation();
	const primary = "#43BD98";

	return (
		<section className="relative py-20 bg-white text-gray-900 font-[Poppins] overflow-hidden">
			<div className="max-w-5xl mx-auto px-6 text-center">
				<motion.p
					initial={{ opacity: 0, y: -20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="uppercase text-sm font-semibold tracking-wide mb-3"
					style={{ color: primary }}
				>
					{t("store.comparison.badge")}
				</motion.p>

				<motion.h2
					initial={{ opacity: 0, y: -20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.1 }}
					viewport={{ once: true }}
					className="font-extrabold text-gray-900 mb-4"
					style={{ fontSize: "clamp(1.8rem, 5vw, 2.4rem)" }}
				>
					{t("store.comparison.title")}
				</motion.h2>

				<motion.p
					initial={{ opacity: 0, y: 10 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					viewport={{ once: true }}
					className="text-gray-600 text-lg mb-12 max-w-2xl mx-auto"
				>
					{t("store.comparison.subtitle")}
				</motion.p>

				{/* Cards */}
				<div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
					{/* App Card */}
					<motion.div
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
						viewport={{ once: true }}
						className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 text-left border border-gray-200"
					>
						<div
							className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
							style={{ backgroundColor: `${primary}15` }}
						>
							<Smartphone className="w-7 h-7" style={{ color: primary }} />
						</div>
						<h3 className="text-xl font-bold text-gray-900 mb-2">
							{t("store.comparison.appCard.title")}
						</h3>
						<p className="text-gray-600 mb-4 text-sm">
							{t("store.comparison.appCard.subtitle")}
						</p>
						<ul className="space-y-2">
							{t("store.comparison.appCard.benefits", {
								returnObjects: true,
							}).map((benefit, i) => (
								<li
									key={i}
									className="flex items-center gap-2 text-gray-700 text-sm"
								>
									<Check className="w-4 h-4" style={{ color: primary }} />
									{benefit}
								</li>
							))}
						</ul>
					</motion.div>

					{/* Web Card */}
					<motion.div
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
						viewport={{ once: true }}
						className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 text-left border border-gray-200"
					>
						<div
							className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
							style={{ backgroundColor: `${primary}15` }}
						>
							<Globe className="w-7 h-7" style={{ color: primary }} />
						</div>
						<h3 className="text-xl font-bold text-gray-900 mb-2">
							{t("store.comparison.webCard.title")}
						</h3>
						<p className="text-gray-600 mb-4 text-sm">
							{t("store.comparison.webCard.subtitle")}
						</p>
						<ul className="space-y-2">
							{t("store.comparison.webCard.benefits", {
								returnObjects: true,
							}).map((benefit, i) => (
								<li
									key={i}
									className="flex items-center gap-2 text-gray-700 text-sm"
								>
									<Check className="w-4 h-4" style={{ color: primary }} />
									{benefit}
								</li>
							))}
						</ul>
					</motion.div>
				</div>

				{/* Final Message */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.3 }}
					viewport={{ once: true }}
					className="mt-12 p-6 rounded-2xl max-w-2xl mx-auto"
					style={{
						backgroundColor: `${primary}10`,
						border: `1px solid ${primary}20`,
					}}
				>
					<p className="text-gray-800 font-medium flex items-center justify-center gap-2 flex-wrap">
						<span>{t("store.comparison.finalMessage")}</span>
						<ArrowRight className="w-4 h-4" style={{ color: primary }} />
						<strong style={{ color: primary }}>
							{t("store.comparison.finalHighlight")}
						</strong>
					</p>
				</motion.div>
			</div>
		</section>
	);
}
